import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface GameState {
  level: number;
  score: number;
  lives: number;
  currentRoom: string;
  inventory: string[];
  completedChallenges: number;
  isGameOver: boolean;
  isVictory: boolean;
}

interface Challenge {
  id: string;
  title: string;
  description: string;
  codeBlocks: string[];
  correctSequence: string[];
  hint: string;
  reward: string;
}

const CodeQuestCastle: React.FC = () => {
  const [gameState, setGameState] = useState<GameState>({
    level: 1,
    score: 0,
    lives: 3,
    currentRoom: 'entrance',
    inventory: [],
    completedChallenges: 0,
    isGameOver: false,
    isVictory: false
  });

  const [selectedBlocks, setSelectedBlocks] = useState<string[]>([]);
  const [currentChallenge, setCurrentChallenge] = useState<Challenge | null>(null);
  const [showHint, setShowHint] = useState(false);
  const [gameMessage, setGameMessage] = useState('');

  const challenges: Challenge[] = [
    {
      id: 'move_forward',
      title: 'Move Forward',
      description: 'Help the knight move forward by arranging the code blocks correctly!',
      codeBlocks: ['move()', 'if (path_clear)', 'repeat(3)', 'turn_left()'],
      correctSequence: ['if (path_clear)', 'move()'],
      hint: 'First check if the path is clear, then move!',
      reward: 'Golden Key'
    },
    {
      id: 'collect_treasure',
      title: 'Collect Treasure',
      description: 'Use a loop to collect all the treasures in the room!',
      codeBlocks: ['collect()', 'repeat(3)', 'if (treasure_here)', 'move()'],
      correctSequence: ['repeat(3)', 'if (treasure_here)', 'collect()', 'move()'],
      hint: 'Use a loop to repeat the treasure collection process!',
      reward: 'Magic Sword'
    },
    {
      id: 'avoid_traps',
      title: 'Avoid Traps',
      description: 'Navigate safely by checking for traps before moving!',
      codeBlocks: ['move()', 'if (trap_ahead)', 'turn_right()', 'jump()'],
      correctSequence: ['if (trap_ahead)', 'jump()', 'move()'],
      hint: 'Always check for traps and jump over them!',
      reward: 'Shield of Protection'
    },
    {
      id: 'unlock_door',
      title: 'Unlock the Door',
      description: 'Use the key to unlock the door to the next room!',
      codeBlocks: ['use_key()', 'if (door_locked)', 'unlock()', 'open_door()'],
      correctSequence: ['if (door_locked)', 'use_key()', 'unlock()', 'open_door()'],
      hint: 'Check if the door is locked, then use your key!',
      reward: 'Crown of Wisdom'
    }
  ];

  const rooms = {
    entrance: {
      name: 'Castle Entrance',
      description: 'You stand before the mighty Code Quest Castle. The door is locked with a magical code lock.',
      challenge: challenges[0]
    },
    hallway: {
      name: 'Treasure Hallway',
      description: 'A long hallway filled with sparkling treasures. But beware of the traps!',
      challenge: challenges[1]
    },
    chamber: {
      name: 'Trap Chamber',
      description: 'A dangerous room with pressure plates and spikes. Tread carefully!',
      challenge: challenges[2]
    },
    throne: {
      name: 'Throne Room',
      description: 'The final room where the princess awaits. Unlock the door to rescue her!',
      challenge: challenges[3]
    }
  };

  const getCurrentRoom = () => {
    return rooms[gameState.currentRoom as keyof typeof rooms];
  };

  const handleBlockSelect = (block: string) => {
    if (selectedBlocks.includes(block)) {
      setSelectedBlocks(selectedBlocks.filter(b => b !== block));
    } else {
      setSelectedBlocks([...selectedBlocks, block]);
    }
  };

  const checkSolution = () => {
    if (!currentChallenge) return;

    const isCorrect = JSON.stringify(selectedBlocks) === JSON.stringify(currentChallenge.correctSequence);
    
    if (isCorrect) {
      setGameState(prev => ({
        ...prev,
        score: prev.score + 100,
        completedChallenges: prev.completedChallenges + 1,
        inventory: [...prev.inventory, currentChallenge.reward]
      }));
      
      setGameMessage(`🎉 Correct! You earned: ${currentChallenge.reward}`);
      
      // Move to next room or complete game
      setTimeout(() => {
        if (gameState.currentRoom === 'throne') {
          setGameState(prev => ({ ...prev, isVictory: true }));
        } else {
          const roomOrder = ['entrance', 'hallway', 'chamber', 'throne'];
          const currentIndex = roomOrder.indexOf(gameState.currentRoom);
          const nextRoom = roomOrder[currentIndex + 1];
          setGameState(prev => ({ ...prev, currentRoom: nextRoom }));
          setCurrentChallenge(rooms[nextRoom as keyof typeof rooms].challenge);
        }
        setSelectedBlocks([]);
        setGameMessage('');
      }, 2000);
    } else {
      setGameState(prev => ({ ...prev, lives: prev.lives - 1 }));
      setGameMessage('❌ Try again! Think about the logic of the code.');
      
      if (gameState.lives <= 1) {
        setTimeout(() => {
          setGameState(prev => ({ ...prev, isGameOver: true }));
        }, 2000);
      }
    }
  };

  const resetGame = () => {
    setGameState({
      level: 1,
      score: 0,
      lives: 3,
      currentRoom: 'entrance',
      inventory: [],
      completedChallenges: 0,
      isGameOver: false,
      isVictory: false
    });
    setSelectedBlocks([]);
    setCurrentChallenge(challenges[0]);
    setGameMessage('');
    setShowHint(false);
  };

  useEffect(() => {
    setCurrentChallenge(getCurrentRoom().challenge);
  }, [gameState.currentRoom]);

  if (gameState.isVictory) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl text-center">
          <div className="text-6xl mb-4">🏆</div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Victory!</h1>
          <p className="text-xl text-gray-700 mb-6">
            Congratulations! You've successfully rescued Princess Pixel and completed the Code Quest Castle!
          </p>
          <div className="bg-yellow-100 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold mb-2">Final Score: {gameState.score}</h3>
            <p className="text-gray-700">Challenges Completed: {gameState.completedChallenges}/4</p>
            <p className="text-gray-700">Items Collected: {gameState.inventory.length}</p>
          </div>
          <div className="space-y-4">
            <button
              onClick={resetGame}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors mr-4"
            >
              Play Again
            </button>
            <Link
              to="/high-code/games"
              className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Back to Games
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (gameState.isGameOver) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-500 to-gray-800 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl text-center">
          <div className="text-6xl mb-4">💀</div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Game Over</h1>
          <p className="text-xl text-gray-700 mb-6">
            The castle's magic was too strong! But don't give up - every programmer learns from mistakes!
          </p>
          <div className="bg-red-100 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold mb-2">Final Score: {gameState.score}</h3>
            <p className="text-gray-700">Challenges Completed: {gameState.completedChallenges}/4</p>
          </div>
          <div className="space-y-4">
            <button
              onClick={resetGame}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors mr-4"
            >
              Try Again
            </button>
            <Link
              to="/high-code/games"
              className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Back to Games
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      {/* Game Header */}
      <div className="bg-black/20 backdrop-blur-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center mt-24">
            <div className="flex items-center space-x-6">
              <h1 className="text-2xl font-bold text-white">🏰 Code Quest Castle</h1>
              <div className="flex space-x-4 text-white">
                <div className="bg-blue-600 px-3 py-1 rounded-full">
                  Score: {gameState.score}
                </div>
                <div className="bg-red-600 px-3 py-1 rounded-full">
                  Lives: {gameState.lives}
                </div>
                <div className="bg-green-600 px-3 py-1 rounded-full">
                  Level: {gameState.level}
                </div>
              </div>
            </div>
            <Link
              to="/high-code/games"
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Back to Games
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Game Area */}
          <div className="lg:col-span-2">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <h2 className="text-2xl font-bold text-white mb-4">
                {getCurrentRoom().name}
              </h2>
              <p className="text-white/80 mb-6">
                {getCurrentRoom().description}
              </p>

              {currentChallenge && (
                <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    Challenge: {currentChallenge.title}
                  </h3>
                  <p className="text-white/80 mb-6">
                    {currentChallenge.description}
                  </p>

                  {/* Code Blocks */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-white mb-3">Available Code Blocks:</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {currentChallenge.codeBlocks.map((block, index) => (
                        <button
                          key={index}
                          onClick={() => handleBlockSelect(block)}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            selectedBlocks.includes(block)
                              ? 'bg-blue-500 border-blue-400 text-white'
                              : 'bg-white/10 border-white/30 text-white hover:bg-white/20'
                          }`}
                        >
                          {block}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Selected Sequence */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-white mb-3">Your Code Sequence:</h4>
                    <div className="bg-black/30 rounded-lg p-4 min-h-[60px] border border-white/20">
                      {selectedBlocks.length === 0 ? (
                        <p className="text-white/50 italic">Drag code blocks here to build your solution...</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {selectedBlocks.map((block, index) => (
                            <span
                              key={index}
                              className="bg-blue-500 text-white px-3 py-1 rounded-lg"
                            >
                              {block}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-4">
                    <button
                      onClick={checkSolution}
                      disabled={selectedBlocks.length === 0}
                      className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-500 disabled:cursor-not-allowed transition-colors"
                    >
                      Run Code
                    </button>
                    <button
                      onClick={() => setShowHint(!showHint)}
                      className="bg-yellow-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-yellow-700 transition-colors"
                    >
                      {showHint ? 'Hide Hint' : 'Show Hint'}
                    </button>
                    <button
                      onClick={() => setSelectedBlocks([])}
                      className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
                    >
                      Clear
                    </button>
                  </div>

                  {/* Hint */}
                  {showHint && (
                    <div className="mt-4 bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-4">
                      <p className="text-yellow-200">
                        💡 <strong>Hint:</strong> {currentChallenge.hint}
                      </p>
                    </div>
                  )}

                  {/* Game Message */}
                  {gameMessage && (
                    <div className="mt-4 bg-blue-500/20 border border-blue-500/50 rounded-lg p-4">
                      <p className="text-blue-200">{gameMessage}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Inventory */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-4">🎒 Inventory</h3>
              {gameState.inventory.length === 0 ? (
                <p className="text-white/60">No items yet...</p>
              ) : (
                <div className="space-y-2">
                  {gameState.inventory.map((item, index) => (
                    <div key={index} className="bg-white/10 rounded-lg p-3 border border-white/20">
                      <span className="text-white">✨ {item}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Progress */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-4">📊 Progress</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-white mb-1">
                    <span>Rooms Explored</span>
                    <span>{gameState.completedChallenges}/4</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(gameState.completedChallenges / 4) * 100}%` }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-white mb-1">
                    <span>Score</span>
                    <span>{gameState.score}</span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-white mb-1">
                    <span>Lives</span>
                    <span>{gameState.lives}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Learning Objectives */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-4">🎯 Learning Goals</h3>
              <div className="space-y-2 text-white/80">
                <p>• Understand conditional statements (if-then)</p>
                <p>• Learn about loops and repetition</p>
                <p>• Practice logical thinking</p>
                <p>• Develop problem-solving skills</p>
                <p>• Experience debugging concepts</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeQuestCastle;
